/**
 * Imperative confirm dialog — a typed, promise-based replacement for the
 * native `window.confirm()`. Mount `<ConfirmDialogHost />` once near the app
 * root; call `confirmDialog({ ... })` from anywhere to await a user choice.
 *
 *     const ok = await confirmDialog({
 *       title: 'Delete leave type?',
 *       message: 'Existing requests for this type will be unassigned.',
 *       confirmLabel: 'Delete',
 *       variant: 'danger',
 *     });
 *     if (!ok) return;
 *
 * Replaces every `if (!confirm('…')) return` site with a Promise-based call
 * that renders the project's `<ConfirmDialog>` (proper styling, focus trap,
 * escape handling). One source of truth — change the look in
 * `ConfirmDialog.tsx` and every confirmation in the app updates.
 */
import { useEffect, useState } from 'react';
import ConfirmDialog from '@/components/ui/ConfirmDialog';

export interface ConfirmOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;     // dismiss button text; defaults to "Cancel"
  variant?: 'danger' | 'primary';
}

interface QueueItem extends ConfirmOptions {
  id: number;
  resolve: (value: boolean) => void;
}

type Listener = (queue: QueueItem[]) => void;

let counter = 0;
let queue: QueueItem[] = [];
const listeners = new Set<Listener>();

function emit() {
  for (const l of listeners) l(queue);
}

function subscribe(l: Listener): () => void {
  listeners.add(l);
  l(queue);
  return () => { listeners.delete(l); };
}

/**
 * Show a confirm dialog. Resolves to `true` when the user confirms, `false`
 * when they cancel / press Esc / click the backdrop. Multiple concurrent
 * calls are queued — only one dialog is visible at a time.
 */
export function confirmDialog(opts: ConfirmOptions): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    queue = [...queue, { ...opts, id: ++counter, resolve }];
    emit();
  });
}

/** Render-once host. Place inside the authenticated AppShell tree. */
export function ConfirmDialogHost() {
  const [items, setItems] = useState<QueueItem[]>(queue);

  useEffect(() => subscribe(setItems), []);

  const current = items[0];
  if (!current) return null;

  const close = (result: boolean) => {
    current.resolve(result);
    queue = queue.filter((q) => q.id !== current.id);
    emit();
  };

  return (
    <ConfirmDialog
      open
      title={current.title}
      message={current.message}
      confirmLabel={current.confirmLabel}
      cancelLabel={current.cancelLabel}
      variant={current.variant}
      onConfirm={() => close(true)}
      onCancel={() => close(false)}
    />
  );
}
